<template>
    <div
        class="soli-stats"
        :class="{
            'soli-stats--glass': glass,
        }"
    >
        <template v-if="loading">
            <v-card
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="stats-card"
                :style="loadingCardStyle"
            >
                <div class="stats-metric">
                    <div class="d-flex flex-column ml-3">
                        <div class="skeleton-bone skeleton-bone--label" />
                        <div class="skeleton-bone skeleton-bone--value" />
                    </div>
                </div>
            </v-card>
        </template>
        <template v-else>
            <v-card
                v-for="metric in allMetrics"
                :key="metric.key"
                class="stats-card"
                :class="'stats-card--' + metric.color"
                :style="cardStyle"
            >
                <div
                    class="stats-metric"
                    :class="{
                        'stats-metric--selected': selected === metric.key,
                    }"
                    @click="
                        emitClick(metric.key);
                        navigate(metric.link);
                    "
                >
                    <div class="d-flex flex-column ml-3">
                        <span class="text-caption text--secondary">{{
                            metric.label
                        }}</span>
                        <div class="d-flex align-center">
                            <span
                                class="text-h5 font-weight-semibold stats-value"
                                >{{ metric.value ?? "—" }}</span
                            >
                            <div
                                v-if="metric.secondary"
                                class="d-flex flex-column ml-2"
                                :class="{
                                    'stats-secondary--clickable':
                                        metric.secondaryLink,
                                }"
                                @click="
                                    metric.secondaryLink
                                        ? [
                                              $event.stopPropagation(),
                                              navigate(metric.secondaryLink),
                                          ]
                                        : undefined
                                "
                            >
                                <span
                                    v-if="metric.secondaryLabel"
                                    class="stats-secondary-label"
                                    >{{ metric.secondaryLabel }}</span
                                >
                                <span
                                    class="d-flex align-center text-caption text--secondary"
                                    >{{ metric.secondary
                                    }}<v-icon
                                        v-if="metric.secondaryLink"
                                        x-small
                                        class="ml-1 stats-secondary-link"
                                        >open_in_new</v-icon
                                    ></span
                                >
                            </div>
                        </div>
                    </div>
                    <trend
                        v-if="metric.sparkData && metric.sparkData.length > 1"
                        class="stats-metric__spark ml-auto"
                        :data="metric.sparkData"
                        :gradient="sparkGradient(metric.color)"
                        :auto-draw="firstLoad"
                        smooth
                    />
                </div>
            </v-card>
        </template>
    </div>
</template>

<script>
export default {
    props: {
        stats: { type: Array, default: () => [] },
        links: { type: Boolean, default: true },
        selected: { type: String, default: null },
        glass: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {
            firstLoad: true,
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.firstLoad = false;
        });
    },
    computed: {
        allMetrics() {
            const colors = ["accent", "success", "secondary", "warning"];
            let idx = 0;
            const metrics = [];
            for (const card of this.stats) {
                for (const metric of card.metrics) {
                    metrics.push({
                        ...metric,
                        color: card.color || colors[idx % colors.length],
                    });
                    idx++;
                }
            }
            return metrics;
        },
        perRow() {
            return Math.ceil(this.allMetrics.length / 2);
        },
        cardStyle() {
            const cols = this.perRow;
            const gapTotal = (cols - 1) * 8;
            return {
                flexBasis: `calc((100% - ${gapTotal}px) / ${cols})`,
            };
        },
        loadingCardStyle() {
            const cols = 3;
            const gapTotal = (cols - 1) * 8;
            return {
                flexBasis: `calc((100% - ${gapTotal}px) / ${cols})`,
            };
        },
    },
    methods: {
        navigate(path) {
            if (this.links && path) this.$inertia.visit(path);
        },
        emitClick(filter) {
            this.$emit("click", { filter });
        },
        sparkGradient(type) {
            const lighten = {
                accent: "--v-accent-lighten1",
                secondary: "--v-secondary-lighten1",
                warning: "--v-warning-lighten1",
                success: "--v-success-lighten1",
            };
            const darken = {
                accent: "--v-accent-darken2",
                secondary: "--v-secondary-darken2",
                warning: "--v-warning-darken2",
                success: "--v-success-darken2",
            };
            return [`var(${lighten[type]})`, `var(${darken[type]})`];
        },
    },
};
</script>

<style scoped>
.soli-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: 1068px;
}

.stats-card {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

/* Color assignments for stat values */
.stats-card--accent .stats-value {
    color: var(--v-accent-darken1);
}

.stats-card--success .stats-value {
    color: var(--v-success-darken2);
}

.stats-card--secondary .stats-value {
    color: var(--v-secondary-darken2);
}

.stats-card--warning .stats-value {
    color: var(--v-warning-darken1);
}

.stats-value {
    font-size: 1.7rem !important;
}

.stats-metric .text--secondary {
    line-height: 1;
}

/* Metric rows */
.stats-metric {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px 8px 12px;
    flex: 1;
    cursor: pointer;
    transition: background 0.15s ease;
    border-radius: inherit;
    position: relative;
    white-space: nowrap;
}

.stats-metric::before {
    content: "";
    position: absolute;
    left: 13px;
    top: 9px;
    bottom: 9px;
    width: 3px;
    border-radius: 2px;
}

.stats-card--accent .stats-metric::before {
    background: var(--v-accent-darken3, var(--v-accent-base));
}

.stats-card--success .stats-metric::before {
    background: var(--v-success-darken3, var(--v-success-base));
}

.stats-card--secondary .stats-metric::before {
    background: var(--v-secondary-darken3, var(--v-secondary-base));
}

.stats-card--warning .stats-metric::before {
    background: var(--v-warning-darken2, var(--v-warning-base));
}

.stats-card--accent .stats-metric:hover {
    background: rgba(var(34, 140, 189), 0.06);
}

.stats-card--success .stats-metric:hover {
    background: rgba(var(50, 192, 144), 0.06);
}

.stats-card--secondary .stats-metric:hover {
    background: rgba(var(100, 100, 140), 0.06);
}

.stats-card--warning .stats-metric:hover {
    background: rgba(var(239, 108, 0), 0.06);
}

.stats-metric__spark {
    width: 64px;
    height: 24px;
    flex-shrink: 0;
    opacity: 0.85;
}

.stats-metric__spark >>> path {
    stroke-width: 4 !important;
}

.stats-secondary-label {
    font-size: 0.55rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.5;
    line-height: 1;
}

.stats-secondary-link {
    opacity: 0.4;
    transition: opacity 0.15s ease;
}

.stats-secondary--clickable {
    cursor: pointer;
    padding: 4px 6px;
    margin: -2px -6px;
    border-radius: 16px;
    transition: background 0.15s ease, opacity 0.15s ease;
}

.stats-secondary--clickable:hover {
    background: var(--glass-bottomnav-slider-bg);
}

.stats-secondary--clickable:hover .stats-secondary-link {
    opacity: 0.8;
}

.stats-metric--selected {
    background: rgba(var(--v-accent-base-rgb, 100, 181, 246), 0.12);
}

/* Glass mode: colored gradients and tinted borders */
.soli-stats--glass .stats-card--accent {
    border: 1px solid rgba(var(--v-accent-base-rgb, 34, 140, 189), 0.2);
    background: linear-gradient(
        135deg,
        rgba(var(--v-accent-base-rgb, 34, 140, 189), 0.08),
        var(--glass-pill-bg)
    );
}

.soli-stats--glass .stats-card--success {
    border: 1px solid rgba(var(--fc-event-weekly, 50, 192, 144), 0.2);
    background: linear-gradient(
        135deg,
        rgba(var(--fc-event-weekly, 50, 192, 144), 0.08),
        var(--glass-pill-bg)
    );
}

.soli-stats--glass .stats-card--secondary {
    border: 1px solid rgba(var(--v-secondary-base-rgb, 100, 100, 140), 0.2);
    background: linear-gradient(
        135deg,
        rgba(var(--v-secondary-base-rgb, 100, 100, 140), 0.08),
        var(--glass-pill-bg)
    );
}

.soli-stats--glass .stats-card--warning {
    border: 1px solid rgba(var(--v-warning-base-rgb, 239, 108, 0), 0.2);
    background: linear-gradient(
        135deg,
        rgba(var(--v-warning-base-rgb, 239, 108, 0), 0.08),
        var(--glass-pill-bg)
    );
}

/* Skeleton loading placeholders */
.skeleton-bone {
    border-radius: 4px;
    background: rgba(128, 128, 128, 0.15);
    animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-bone--label {
    width: 72px;
    height: 10px;
    margin-bottom: 8px;
}

.skeleton-bone--value {
    width: 52px;
    height: 20px;
}

@keyframes skeleton-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.4;
    }
}

@media (max-width: 960px) {
    .soli-stats {
        flex-direction: column;
    }

    .stats-card {
        max-width: none;
        flex-basis: auto;
    }
}
</style>
